import { useState, useCallback, useMemo, memo, ReactNode, Dispatch, SetStateAction } from "react";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { Badge } from "./badge";
import { Button, ButtonProps } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useUncontrolledState } from "../hooks/use-uncontrolled-state";
import { Spinner } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";

export interface MultiSelectOption<T = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

export interface MultiSelectProps<T = string> extends Pick<ButtonProps, "style" | "autoFocus"> {
  id?: string;
  options: MultiSelectOption<T>[];
  value: T[];
  onValueChange: (value: T[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  maxCount?: number;
  maxDisplayCount?: number;
  modalPopover?: boolean;
  keepSelectedInList?: boolean;
  creator?: {
    render: (inputValue: string) => ReactNode;
    isValidNewOption?: (inputValue: string, selectValue: T[]) => boolean;
    transform: (inputValue: string) => T;
    onCreate: (newOption: MultiSelectOption<T>) => void;
  };
  searchKeywords?: (option: MultiSelectOption<T>) => string[];
  renderOption?: (option: MultiSelectOption<T>) => ReactNode;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  shouldFilter?: boolean;
  isLoading?: boolean;
  onBlur?: () => void;
  showAsBadges?: boolean;
  minPopoverWidth?: string | number;
  clearSearchOnSelect?: boolean;
  badgeListOutside?: boolean; // temporary before UI library audit
  badgeListClassName?: string;
}

/**
 * A multi-select component that allows users to select multiple options from a dropdown list.
 * Selected items are displayed as removable badges within the input field.
 *
 * @param options - (Required) Array of options to select from
 * @param value - (Required) Currently selected values
 * @param onValueChange - (Required) Callback function called when selection changes
 * @param id - (Optional) Id to be passed to the input element
 * @param placeholder - (Optional) Placeholder text shown when no items are selected. Defaults to "Select items..."
 * @param searchPlaceholder - (Optional) Placeholder text for the search input in dropdown. Defaults to "Search..."
 * @param emptyText - (Optional) Text shown when no search results are found. Defaults to "No results found."
 * @param className - (Optional) Additional CSS classes to apply to the component
 * @param disabled - (Optional) Whether the component is disabled and non-interactive. Defaults to false
 * @param maxCount - (Optional) Maximum number of items that can be selected. Defaults to unlimited
 * @param maxDisplayCount - (Optional) Maximum number of selected items to display as badges before showing "+X more" badge. Defaults to unlimited
 * @param modalPopover - (Optional) Whether to use modal popover behavior that traps focus. Defaults to false
 * @param keepSelectedInList - (Optional) Whether to keep selected items visible in the dropdown list with checkmarks. When false, selected items are hidden from the list. Note: This is automatically forced to true when maxDisplayCount is less than the total number of options to ensure users can see what's selected when overflow badges are shown. Defaults to true
 * @param creator - (Optional) Configuration object for allowing users to create new options by typing:
 *   - `render`: (Required) Function that returns JSX to display the "create new" option in the dropdown (e.g., "Create: {inputValue}")
 *   - `isValidNewOption`: (Optional) Function to validate if the typed input can be converted to a new option. Receives the search input and current selected values. Defaults to always valid if not provided
 *   - `transform`: (Required) Function that converts the typed string input into the option value type T
 *   - `onCreate`: (Required) Callback function called when a new option is created. Receives the new option object with label and value. Use this to add the new option to your options list.
 * @param searchKeywords - (Optional) Callback function to get a list of searchable strings for each option
 * @param renderOption - (Optional) Callback to render custom content for each option in the dropdown list. Receives the option and returns ReactNode rendered in place of the default label (the selection checkbox is still rendered). Defaults to rendering `option.label`.
 * @param searchQuery - (Optional) Controlled search query value. When provided with setSearchQuery, the component operates in controlled search mode
 * @param setSearchQuery - (Optional) Callback function called when search query changes. When provided with searchQuery, the component operates in controlled search mode
 * @param shouldFilter - (Optional) When false, disables the built-in Command component filtering. Useful when you want to handle filtering externally (e.g., server-side filtering). Defaults to false
 * @param isLoading - (Optional) When true, a loading state will be displayed instead of the list of options
 * @param onBlur - (Optional) Callback function called when the popover closes
 * @param showAsBadges - (Optional) When true, selected items are displayed as removable badges. When false, selected items are displayed as a comma-separated string. Defaults to true
 * @param clearSearchOnSelect - (Optional) When true, the search input is cleared after an option is selected. Defaults to true
 * @param badgeListOutside - (Optional) When true, selected badges are rendered in a flex row below the input, and the input itself shows a "{{amount}} selected" summary. Defaults to false
 */
const MultiSelectInner = <T = string,>({
  id,
  options,
  value,
  onValueChange,
  placeholder = "Select items...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  className = "w-full",
  disabled = false,
  maxCount = Number.MAX_SAFE_INTEGER,
  maxDisplayCount = Number.MAX_SAFE_INTEGER,
  modalPopover = false,
  keepSelectedInList = true,
  creator,
  searchKeywords,
  renderOption,
  searchQuery,
  setSearchQuery,
  shouldFilter = true,
  isLoading,
  onBlur,
  showAsBadges = true,
  minPopoverWidth,
  clearSearchOnSelect = true,
  badgeListOutside = false,
  badgeListClassName,
  ...rest
}: MultiSelectProps<T>) => {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useUncontrolledState({
    state: searchQuery ?? "",
    setState: setSearchQuery,
  });

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      // Call onBlur when the popover closes
      if (!newOpen && onBlur) {
        onBlur();
      }
    },
    [onBlur]
  );

  // Force keepSelectedInList to true when maxDisplayCount is less than options length
  // This ensures users can see what's selected when overflow badges are shown
  const effectiveKeepSelectedInList = maxDisplayCount < options.length ? true : keepSelectedInList;

  const handleUnselect = useCallback(
    (item: T) => {
      onValueChange(value.filter((i) => i !== item));
    },
    [onValueChange, value]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = e.target as HTMLInputElement;
      if (input.value === "") {
        if (e.key === "Delete" || e.key === "Backspace") {
          const last = value[value.length - 1];
          if (last !== undefined) {
            handleUnselect(last);
          }
        }
        if (e.key === "Escape") {
          input.blur();
        }
      }
    },
    [value, handleUnselect]
  );

  const selectables = effectiveKeepSelectedInList ? options : options.filter((option) => !value.includes(option.value));

  const selected = useMemo(
    () =>
      value.map((item) => options.find((option) => option.value === item)).filter(Boolean) as MultiSelectOption<T>[],
    [value, options]
  );

  const handleValueToggle = useCallback(
    (newVal: T) => {
      const valueIsSelected = value.some((value) => value === newVal);

      if (!valueIsSelected && value.length >= maxCount) {
        return;
      }
      onValueChange(valueIsSelected ? value.filter((value) => value !== newVal) : [...value, newVal]);
    },
    [value, onValueChange, maxCount]
  );

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "group flex h-auto min-h-9 items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm transition-colors",
              "placeholder:text-muted-foreground",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
            disabled={disabled}
            onClick={() => handleOpenChange(!open)}
            {...rest}
          >
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              {selected.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
              {badgeListOutside ? (
                selected.length > 0 && (
                  <span className="truncate">{t("{{amount}} selected", { amount: selected.length })}</span>
                )
              ) : showAsBadges ? (
                <>
                  {selected.slice(0, maxDisplayCount).map((item) => (
                    <Badge
                      variant="secondary"
                      key={JSON.stringify(item.value)}
                      className={cn(
                        "mr-1 max-w-full group-hover:bg-background group-hover:text-foreground",
                        badgeListClassName
                      )}
                    >
                      <span className="truncate">{item.label}</span>
                      {!item.disabled && (
                        <div
                          role="button"
                          className="ml-1 flex-shrink-0 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleUnselect(item.value);
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUnselect(item.value);
                          }}
                          data-testid="remove-selected-item"
                        >
                          <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                        </div>
                      )}
                    </Badge>
                  ))}
                  {selected.length > maxDisplayCount && (
                    <Badge variant="secondary" className="mr-1 group-hover:bg-background group-hover:text-foreground">
                      {t("+{{amount}} more", { amount: selected.length - maxDisplayCount })}
                    </Badge>
                  )}
                </>
              ) : (
                selected.length > 0 && (
                  <span className="truncate">
                    {selected
                      .slice(0, maxDisplayCount)
                      .map((item) => item.label)
                      .join(", ")}
                    {selected.length > maxDisplayCount && ` +${selected.length - maxDisplayCount} more`}
                  </span>
                )
              )}
            </div>
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full p-0"
          align="start"
          style={{
            width: "var(--radix-popover-trigger-width)",
            minWidth: minPopoverWidth,
          }}
          onWheelCapture={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={shouldFilter}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              onKeyDown={handleKeyDown}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? (
                  <div className="flex items-center justify-center w-full">
                    <Spinner size="sm" color="secondary" />
                  </div>
                ) : (
                  emptyText
                )}
              </CommandEmpty>
              <CommandGroup>
                {!!creator && search && (creator.isValidNewOption?.(search, value) ?? true) && (
                  <CommandItem
                    value={search}
                    onSelect={() => {
                      const transformedValue = creator.transform(search);
                      const newOption: MultiSelectOption<T> = {
                        label: search,
                        value: transformedValue,
                      };

                      // Call onCreate callback
                      creator.onCreate(newOption);

                      setSearch("");
                      onValueChange([...value, transformedValue]);
                      handleOpenChange(false);
                    }}
                  >
                    {creator.render(search)}
                  </CommandItem>
                )}
                {selectables.map((option) => (
                  <CommandItem
                    key={JSON.stringify(option.value)}
                    value={String(option.value)}
                    disabled={option.disabled || (value.length >= maxCount && !value.includes(option.value))}
                    onSelect={() => {
                      handleValueToggle(option.value);
                      if (clearSearchOnSelect) {
                        setSearch("");
                      }
                    }}
                    keywords={searchKeywords ? searchKeywords(option) : [option.label, String(option.value)]}
                  >
                    {effectiveKeepSelectedInList && (
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          value.includes(option.value)
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <CheckIcon className="h-2 w-2 text-primary-foreground" />
                      </div>
                    )}
                    {renderOption ? renderOption(option) : option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {badgeListOutside && selected.length > 0 && (
        <div className={cn("flex flex-row flex-wrap items-center gap-1 mt-2", badgeListClassName)}>
          {selected.map((item) => (
            <Badge variant="secondary" key={JSON.stringify(item.value)} className="max-w-full">
              <span className="truncate">{item.label}</span>
              {!item.disabled && (
                <div
                  role="button"
                  className="ml-1 flex-shrink-0 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUnselect(item.value);
                    }
                  }}
                  onClick={() => handleUnselect(item.value)}
                  data-testid="remove-selected-item"
                >
                  <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </div>
              )}
            </Badge>
          ))}
        </div>
      )}
    </>
  );
};

// Memoized so the (often several) MultiSelects on a form skip re-rendering when the parent form
// re-renders on an unrelated field's keystroke. Callers must pass stable `options`/`value`/handler
// identities (e.g. via useMemo/useCallback) for the bailout to take effect. The cast preserves the
// generic call signature that `memo` would otherwise erase.
export const MultiSelect = memo(MultiSelectInner) as typeof MultiSelectInner;
